import React, { useCallback, useEffect } from 'react';

import { Grid, Section } from '@gogame/components';
import { FormField, SidePageV2, withForm } from '@gogame/form';
import Permissions from '@gogame/permissions';

import { useUploadDPA, useUploadNDA } from 'scenes/BusinessPartners/data';
import {
  loadUsers,
  loadActiveUsers,
  loadPartnerCompany,
  loadPartnerCompanies,
  loadActivePartnerCompanies,
  loadCustomerCompany,
  loadInvoiceLevels,
  loadIOTypes,
  loadCurrency,
  loadExpenseTypes,
  loadNetworks,
  loadPaymentTerms,
  loadCountry,
  loadTimeZone,
} from 'scenes/BusinessPartners/sources/fields';

import {
  NAME,
  OWNER,
  PARTNER_COMPANY,
  CUSTOMER_COMPANY,
  INVOICE_LEVEL,
  IO_TYPE,
  VAT_NUMBER,
  TAX_ID,
  CURRENCY,
  EXPENSE_TYPES,
  NETWORK,
  PAYMENT_TERM,
  DPA,
  DPA_PERIOD,
  ADDRESS,
  STREET_ADDRESS,
  STREET_ADDRESS_2,
  CITY,
  REGION,
  POSTAL_CODE,
  COUNTRY,
  EMAIL,
  MOBILE,
  PHONE,
  SKYPE,
  CONTACT_PERSON,
  FIRST_NAME,
  LAST_NAME,
  TIME_ZONE,
  NDA,
} from './constants';
import { BusinessPartnerFormValidationSchema } from './schemas';
import { InvoiceProcessingRuleSection } from './InvoiceProcessingRuleSection';
const isOwnerBlocked = ({ isBlocked }) => isBlocked;

const BusinessPartnerForm = withForm({
  validationSchema: BusinessPartnerFormValidationSchema,
  enableReinitialize: true,
})(({
  children,
  title,
  subTitle,
  loading,
  editableFields,
  onClose,
  values,
  dirty,
  setFieldValue,
  isEdit,
  onExited,
  open,
  entityId,
}) => {
  const uploadDPA = useUploadDPA();
  const uploadNDA = useUploadNDA();

  const partnerCompanyValue = values[PARTNER_COMPANY];
  const loadOnChangePartnerCompany = useCallback(
    async ({ id }) => {
      const { address, timeZone } = await loadPartnerCompany({ id });
      setFieldValue(ADDRESS, address);
      setFieldValue(TIME_ZONE, timeZone);
    },
    [setFieldValue]
  );

  useEffect(() => {
    if ((dirty || !values[CONTACT_PERSON]) && partnerCompanyValue) {
      loadOnChangePartnerCompany({ id: partnerCompanyValue });
    }
  }, [partnerCompanyValue]);

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title
        intensity={400}
        title={title}
        subTitle={subTitle}
        entityId={entityId}
      />
      <Permissions actions={editableFields}>
        {({ isActionAllowed }) => (
          <SidePageV2.NewBody>
            <Section title={'General'}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Name'}
                    padding={'0'}
                    name={NAME}
                    placeholder={"Partner's business name"}
                    disabled={!isActionAllowed(NAME)}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Owner'}
                    padding={'0'}
                    name={OWNER}
                    source={isEdit ? loadUsers : loadActiveUsers}
                    disabled={!isActionAllowed(OWNER)}
                    lazy={true}
                    isOptionDisabled={isOwnerBlocked}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Partner company'}
                    padding={'0'}
                    name={PARTNER_COMPANY}
                    source={
                      isEdit ? loadPartnerCompanies : loadActivePartnerCompanies
                    }
                    disabled={!isActionAllowed(PARTNER_COMPANY)}
                    lazy={true}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Customer company'}
                    padding={'0'}
                    name={CUSTOMER_COMPANY}
                    source={loadCustomerCompany}
                    disabled={!isActionAllowed(CUSTOMER_COMPANY)}
                    lazy={true}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Invoice level'}
                    padding={'0'}
                    name={INVOICE_LEVEL}
                    source={loadInvoiceLevels}
                    disabled={!isActionAllowed(INVOICE_LEVEL)}
                    lazy={true}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'IO type'}
                    padding={'0'}
                    name={IO_TYPE}
                    source={loadIOTypes}
                    disabled={!isActionAllowed(IO_TYPE)}
                    lazy={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'VAT number'}
                    padding={'0'}
                    name={VAT_NUMBER}
                    disabled={!isActionAllowed(VAT_NUMBER)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Tax ID'}
                    padding={'0'}
                    name={TAX_ID}
                    disabled={!isActionAllowed(TAX_ID)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Currency'}
                    padding={'0'}
                    name={CURRENCY}
                    source={loadCurrency}
                    disabled={!isActionAllowed(CURRENCY)}
                    lazy={true}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.MultiSelect
                    label={'Expense types'}
                    padding={'0'}
                    name={EXPENSE_TYPES}
                    source={loadExpenseTypes}
                    disabled={!isActionAllowed(EXPENSE_TYPES)}
                    lazy={true}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Network'}
                    padding={'0'}
                    name={NETWORK}
                    source={loadNetworks}
                    disabled={!isActionAllowed(NETWORK)}
                    lazy={true}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Payment term'}
                    padding={'0'}
                    name={PAYMENT_TERM}
                    source={loadPaymentTerms}
                    disabled={!isActionAllowed(PAYMENT_TERM)}
                    lazy={true}
                  />
                </Grid>
              </Grid>
            </Section>
            <Grid container direction={'row'} spacing={3}>
              <Grid item xs={4}>
                <Section title={'DPA'}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormField.AttachmentField
                        label={'Select DPA'}
                        accept={'.pdf, .docx'}
                        tooltip={'Only docx or pdf files are allowed'}
                        name={DPA}
                        padding={0}
                        onUpload={uploadDPA}
                        variant={'outlined'}
                      />
                    </Grid>
                    {values[DPA]?.name ? (
                      <Grid item xs={12}>
                        <FormField.DateRangePicker
                          label={'DPA period'}
                          name={`${DPA}.${DPA_PERIOD}`}
                          padding={0}
                        />
                      </Grid>
                    ) : null}
                  </Grid>
                </Section>
              </Grid>

              <Grid item xs={4}>
                <Section title={'NDA'}>
                  <FormField.AttachmentField
                    label={'Select NDA'}
                    accept={'.pdf, .docx'}
                    tooltip={'Only docx or pdf files are allowed'}
                    name={NDA}
                    disabled={values[NDA] && isEdit}
                    onUpload={uploadNDA}
                    variant={'outlined'}
                    padding={0}
                  />
                </Section>
              </Grid>
            </Grid>
            <Section title={'Address'}>
              <Grid container spacing={3}>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Country'}
                    padding={'0'}
                    name={`${ADDRESS}.${COUNTRY}`}
                    source={loadCountry}
                    disabled={!isActionAllowed(COUNTRY)}
                    lazy={true}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'State / Province / Region'}
                    padding={'0'}
                    name={`${ADDRESS}.${REGION}`}
                    disabled={!isActionAllowed(ADDRESS)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'City'}
                    padding={'0'}
                    name={`${ADDRESS}.${CITY}`}
                    disabled={!isActionAllowed(ADDRESS)}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Zip / Postal code'}
                    padding={'0'}
                    name={`${ADDRESS}.${POSTAL_CODE}`}
                    disabled={!isActionAllowed(ADDRESS)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Address line 1'}
                    padding={'0'}
                    name={`${ADDRESS}.${STREET_ADDRESS}`}
                    placeholder={'Street address, P.O. box, company name, c/o'}
                    disabled={!isActionAllowed(ADDRESS)}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Address line 2'}
                    padding={'0'}
                    name={`${ADDRESS}.${STREET_ADDRESS_2}`}
                    placeholder={
                      'Apartment, suite, unit, building, floor, etc.'
                    }
                    disabled={!isActionAllowed(ADDRESS)}
                  />
                </Grid>
              </Grid>
            </Section>
            <Section title={'Contacts'}>
              <Grid item container spacing={3}>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Email'}
                    padding={'0'}
                    name={`${ADDRESS}.${EMAIL}`}
                    disabled={!isActionAllowed(ADDRESS)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Phone'}
                    padding={'0'}
                    name={`${ADDRESS}.${PHONE}`}
                    disabled={!isActionAllowed(ADDRESS)}
                  />
                </Grid>
              </Grid>
            </Section>
            <Section title={'Legal Contact Person'}>
              <Grid item container spacing={3}>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'First name'}
                    padding={'0'}
                    name={`${CONTACT_PERSON}.${FIRST_NAME}`}
                    disabled={!isActionAllowed(CONTACT_PERSON)}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Last name'}
                    padding={'0'}
                    name={`${CONTACT_PERSON}.${LAST_NAME}`}
                    disabled={!isActionAllowed(CONTACT_PERSON)}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Email'}
                    padding={'0'}
                    name={`${CONTACT_PERSON}.${EMAIL}`}
                    disabled={!isActionAllowed(CONTACT_PERSON)}
                    required={true}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Mobile'}
                    padding={'0'}
                    name={`${CONTACT_PERSON}.${MOBILE}`}
                    disabled={!isActionAllowed(CONTACT_PERSON)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Phone'}
                    padding={'0'}
                    name={`${CONTACT_PERSON}.${PHONE}`}
                    disabled={!isActionAllowed(CONTACT_PERSON)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.TextField
                    label={'Skype'}
                    padding={'0'}
                    name={`${CONTACT_PERSON}.${SKYPE}`}
                    disabled={!isActionAllowed(CONTACT_PERSON)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormField.Select
                    label={'Time zone'}
                    padding={'0'}
                    name={TIME_ZONE}
                    source={loadTimeZone}
                    disabled={!isActionAllowed(TIME_ZONE)}
                    lazy={true}
                  />
                </Grid>
              </Grid>
            </Section>
            <InvoiceProcessingRuleSection />
          </SidePageV2.NewBody>
        )}
      </Permissions>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default BusinessPartnerForm;
